import api from './client'
import type { Company, CompanyOverview, EdgeConfigResult, EdgeCycleEdit, EdgeDevice, EdgeOperationEdit, EdgeLine, EdgeLineEdit, EdgeMachineEdit, EdgeMachineItemEdit, EdgeServer, MachineItemKind, LicenseIssuance, HarborImage, HarborTagDto, MachineLogType, ServerCommandType, ServerLogsPage, SurveySiteSummary, SurveySiteDetail, SurveyEquipmentDetail, SurveyEquipmentUpdate, DeviceSpecSummary, DeviceSpecDetail, EdgeChannelType, EdgeDeviceChannel, EdgeDeviceEdit, EdgeTagEdit } from '../types'

export interface ServerLogsParams {
  type?: MachineLogType
  machineCode?: string
  code?: string
  from?: string
  to?: string
  cursor?: string
  pageSize?: number
}

export const companiesApi = {
  getAll: () => api.get<Company[]>('/companies'),
  getById: (id: string) => api.get<Company>(`/companies/${id}`),
  create: (name: string, code: string) => api.post<Company>('/companies', { name, code }),
  update: (id: string, name: string, code: string, isActive: boolean, enabledApps?: string[]) =>
    api.put<Company>(`/companies/${id}`, { name, code, isActive, enabledApps }),
  delete: (id: string) => api.delete(`/companies/${id}`),
  invite: (id: string, email: string) => api.post(`/companies/${id}/invite`, { email }),
  getOverview: () => api.get<CompanyOverview[]>('/companies/overview'),
}

export const usersApi = {
  getAll: () => api.get('/users'),
  invite: (email: string, companyId?: string) => api.post('/users/invite', { email, companyId }),
  // scopes·companyId 는 실제 Admin 만 보낼 수 있다(그 외 403). 생략하면 기존 값 유지.
  update: (id: string, name: string, role: string, scopes?: string[], companyId?: string) =>
    api.put(`/users/${id}`, { name, role, scopes, companyId }),
  delete: (id: string) => api.delete(`/users/${id}`),
  revokeTokens: (id: string) => api.post(`/users/${id}/revoke-tokens`),
}

export const serversApi = {
  getAll: () => api.get<EdgeServer[]>('/servers'),
  getById: (id: string) => api.get<EdgeServer>(`/servers/${id}`),
  create: (name: string, companyId?: string, ipAddress?: string, memo?: string, externalUrl?: string) =>
    api.post<EdgeServer>('/servers', { name, companyId, ipAddress, memo, externalUrl }),
  update: (id: string, ipAddress?: string, memo?: string, externalUrl?: string, companyId?: string) =>
    api.put<EdgeServer>(`/servers/${id}`, { ipAddress, memo, externalUrl, companyId }),
  delete: (id: string) => api.delete(`/servers/${id}`),
  getServerLogs: (id: string, params: ServerLogsParams = {}) =>
    api.get<ServerLogsPage>(`/servers/${id}/logs`, { params }),
  // 원격 모니터링 시작/중지 — 202 { commandId }, 결과는 SignalR ServerCommandCompleted 로 수신
  sendCommand: (id: string, type: ServerCommandType) =>
    api.post<{ commandId: string }>(`/servers/${id}/commands`, { type }),
  // 디바이스·태그 설정 — 엣지에 SignalR 로 즉시 요청 (오프라인 409 / 무응답 504)
  getDevices: (id: string) =>
    api.get<EdgeDevice[]>(`/servers/${id}/devices`),
  // 라인 › 설비 마스터데이터 — 엣지에 즉시 요청 (Edge팀이 아니면 설비 이름만)
  getMachines: (id: string) =>
    api.get<EdgeLine[]>(`/servers/${id}/machines`),
  // 설비의 Alarm/Mete/Parameter/Signal 목록 통째 교체 — 엣지에 바로 저장 (실패 400 { error })
  updateMachineItems: (id: string, machineCode: string, kind: MachineItemKind, items: EdgeMachineItemEdit[]) =>
    api.put<EdgeConfigResult>(`/servers/${id}/machines/${encodeURIComponent(machineCode)}/${kind}`, items),
  updateMachineOperation: (id: string, machineCode: string, operation: EdgeOperationEdit) =>
    api.put<EdgeConfigResult>(`/servers/${id}/machines/${encodeURIComponent(machineCode)}/operation`, operation),
  updateMachineCycle: (id: string, machineCode: string, cycle: EdgeCycleEdit) =>
    api.put<EdgeConfigResult>(`/servers/${id}/machines/${encodeURIComponent(machineCode)}/cycle`, cycle),
  // 라인·설비 구성 — 엣지에 바로 저장 (실패 400 { error }). 라인을 지우면 안의 설비도 지워진다
  createLine: (id: string, line: EdgeLineEdit) =>
    api.post<EdgeConfigResult>(`/servers/${id}/lines`, line),
  updateLine: (id: string, lineId: string, line: EdgeLineEdit) =>
    api.put<EdgeConfigResult>(`/servers/${id}/lines/${encodeURIComponent(lineId)}`, line),
  deleteLine: (id: string, lineId: string) =>
    api.delete<EdgeConfigResult>(`/servers/${id}/lines/${encodeURIComponent(lineId)}`),
  createMachine: (id: string, machine: EdgeMachineEdit) =>
    api.post<EdgeConfigResult>(`/servers/${id}/machines`, machine),
  updateMachine: (id: string, machine: EdgeMachineEdit) =>
    api.put<EdgeConfigResult>(`/servers/${id}/machines/${encodeURIComponent(machine.code)}`, machine),
  deleteMachine: (id: string, machineCode: string) =>
    api.delete<EdgeConfigResult>(`/servers/${id}/machines/${encodeURIComponent(machineCode)}`),
  // 디바이스(채널)·태그 — 엣지에 바로 저장 (실패 400 { error }). 채널 설정은 엣지 channelInfo JSON 그대로
  getChannelTypes: (id: string) =>
    api.get<EdgeChannelType[]>(`/servers/${id}/channel-types`),
  getDeviceTemplate: (id: string, channelType: string) =>
    api.get<EdgeDeviceChannel>(`/servers/${id}/channel-types/${encodeURIComponent(channelType)}/template`),
  getDeviceChannel: (id: string, code: string) =>
    api.get<EdgeDeviceChannel>(`/servers/${id}/devices/${encodeURIComponent(code)}/channel`),
  createDevice: (id: string, device: EdgeDeviceEdit) =>
    api.post<EdgeConfigResult>(`/servers/${id}/devices`, device),
  updateDevice: (id: string, device: EdgeDeviceEdit) =>
    api.put<EdgeConfigResult>(`/servers/${id}/devices/${encodeURIComponent(device.code)}`, device),
  deleteDevice: (id: string, code: string) =>
    api.delete<EdgeConfigResult>(`/servers/${id}/devices/${encodeURIComponent(code)}`),
  updateDeviceTags: (id: string, code: string, tags: EdgeTagEdit[]) =>
    api.put<EdgeConfigResult>(`/servers/${id}/devices/${encodeURIComponent(code)}/tags`, tags),
}

export const licensesApi = {
  getAll: () => api.get<LicenseIssuance[]>('/licenses'),
  getById: (id: string) => api.get<LicenseIssuance>(`/licenses/${id}`),
  issueCloud: (serverId: string, expiresAt: string, maxNodeCount: number) =>
    api.post<LicenseIssuance>(`/licenses/cloud/${serverId}`, { expiresAt, maxNodeCount }),
  issueLocal: (data: { companyName: string; expiresAt: string; maxNodeCount: number; note?: string }) =>
    api.post<LicenseIssuance>('/licenses/local', data),
  download: (id: string) => api.get(`/licenses/${id}/download`, { responseType: 'blob' }),
  delete: (id: string) => api.delete(`/licenses/${id}`),
}

export const surveysApi = {
  getSites: () => api.get<SurveySiteSummary[]>('/surveys/sites'),
  getSite: (id: string) => api.get<SurveySiteDetail>(`/surveys/sites/${id}`),
  renameSite: (id: string, name: string) => api.put<SurveySiteDetail>(`/surveys/sites/${id}`, { name }),
  deleteSite: (id: string) => api.delete(`/surveys/sites/${id}`),
  updateEquipment: (id: string, data: SurveyEquipmentUpdate) =>
    api.put<SurveyEquipmentDetail>(`/surveys/equipment/${id}`, data),
  deleteEquipment: (id: string) => api.delete(`/surveys/equipment/${id}`),
  // 최신 모바일 업로드 리비전으로 되돌리기 (수동 수정 내용은 사라짐)
  reimportEquipment: (id: string) => api.post<SurveyEquipmentDetail>(`/surveys/equipment/${id}/reimport`),
  // 사진은 인증 헤더가 필요해 <img src>로 직접 못 쓰고 blob으로 받아 object URL로 표시
  getPhoto: (id: string) => api.get(`/surveys/photos/${id}`, { responseType: 'blob' }),
}

export const harborApi = {
  getImages: () => api.get<HarborImage[]>('/harbor/images'),
  register: (repository: string, imageName: string) =>
    api.post<HarborImage>('/harbor/images', { repository, imageName }),
  sync: () => api.post('/harbor/sync'),
  getServerVersions: (serverId: string) => api.get(`/harbor/servers/${serverId}/versions`),
  getOutdated: () => api.get('/harbor/outdated'),
  getImageTags: (imageId: string) => api.get<HarborTagDto[]>(`/harbor/images/${imageId}/tags`),
  retargetLatest: (imageId: string, targetTag: string) =>
    api.post(`/harbor/images/${imageId}/retarget`, { targetTag }),
}

// 디바이스 명세서 — Edge팀 전용. 편집(저장)은 SpecHub 실시간 공동 편집 (deviceSpec/specSync.ts)
export const deviceSpecsApi = {
  getAll: () => api.get<DeviceSpecSummary[]>('/device-specs'),
  get: (id: string) => api.get<DeviceSpecDetail>(`/device-specs/${id}`),
  create: (name: string, companyId: string | null, memo?: string) =>
    api.post<DeviceSpecDetail>('/device-specs', { name, companyId, memo }),
  delete: (id: string) => api.delete(`/device-specs/${id}`),
}
